/**
 * CENTRALIZED TIER CONFIGURATION
 * 
 * This is the single source of truth for all tier-related information.
 * All tier checks, feature access, limits, and AI models should reference this file.
 * 
 * When adding new features or modifying limits:
 * 1. Add/update the feature in TIER_FEATURES
 * 2. Add/update limits in TIER_LIMITS
 * 3. Update FEATURE_REGISTRY if it's a gated feature
 * 
 * Backend: supabase/functions/_shared/tiers.ts mirrors critical parts
 * Database: check_usage_limits_detailed must stay in sync with TIER_LIMITS
 */

// =============================================================================
// TIER DEFINITIONS
// =============================================================================

export const TIERS = ['free', 'pro', 'chef_master'] as const;
export type SubscriptionTier = typeof TIERS[number];

export const TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro Chef',
  chef_master: 'Chef Master',
};

export const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  free: 0,
  pro: 1,
  chef_master: 2,
};

// =============================================================================
// FEATURE DEFINITIONS
// =============================================================================

/**
 * All features that can be gated by tier.
 * Use these keys for hasFeature() checks.
 */
export const FEATURE_KEYS = {
  // AI Features
  unlimitedAi: 'unlimited_ai',
  premiumAi: 'premium_ai',
  advancedAi: 'advanced_ai',
  priorityAiQueue: 'priority_ai_queue',
  voiceGuidance: 'voice_guidance',
  
  // Planning Features
  multiWeekPlanning: 'multi_week_planning',
  aiMealPlan: 'ai_meal_plan',
  
  // Content Features
  recipeAnalytics: 'recipe_analytics',
  bulkImport: 'bulk_import',
  nutritionCalculator: 'nutrition_calculator',
  recipeTranslation: 'recipe_translation',
  recipeImport: 'recipe_import',
  
  // Social Features
  familySharing: 'family_sharing',
  teamAccounts: 'team_accounts', // Legacy alias
  
  // Bulk
  bulkAIActions: 'bulk_ai_actions',
  
  // Misc
  noAds: 'no_ads',
  prioritySupport: 'priority_support',
  offlineAccess: 'offline_access',
  
  // Premium Profile Features
  pinnedRecipes: 'pinned_recipes',
  profileTheme: 'profile_theme',
  profileBanner: 'profile_banner',
  profileAnalytics: 'profile_analytics',
  verifiedBadge: 'verified_badge',
  
  // Livestream Features
  livestreamWatch: 'livestream_watch',
  livestreamHost: 'livestream_host',
  livestreamVod: 'livestream_vod',
  livestreamMembersOnly: 'livestream_members_only',
  aiStreamCompanion: 'ai_stream_companion',
  creatorTipPage: 'creator_tip_page',
} as const;

export type FeatureKey = typeof FEATURE_KEYS[keyof typeof FEATURE_KEYS];

/**
 * Feature access by tier
 * true = full access, false = no access, 'limited' = has limits
 */
export const TIER_FEATURES: Record<SubscriptionTier, Record<FeatureKey, boolean | 'limited'>> = {
  free: {
    [FEATURE_KEYS.unlimitedAi]: false,
    [FEATURE_KEYS.premiumAi]: false,
    [FEATURE_KEYS.advancedAi]: false,
    [FEATURE_KEYS.priorityAiQueue]: false,
    [FEATURE_KEYS.voiceGuidance]: false,
    [FEATURE_KEYS.multiWeekPlanning]: false,
    [FEATURE_KEYS.aiMealPlan]: 'limited',
    [FEATURE_KEYS.recipeAnalytics]: false,
    [FEATURE_KEYS.bulkImport]: false,
    [FEATURE_KEYS.bulkAIActions]: false,
    [FEATURE_KEYS.nutritionCalculator]: false,
    [FEATURE_KEYS.recipeTranslation]: false,
    [FEATURE_KEYS.recipeImport]: false,
    [FEATURE_KEYS.familySharing]: false,
    [FEATURE_KEYS.teamAccounts]: false,
    [FEATURE_KEYS.noAds]: false,
    [FEATURE_KEYS.prioritySupport]: false,
    [FEATURE_KEYS.offlineAccess]: false,
    [FEATURE_KEYS.pinnedRecipes]: false,
    [FEATURE_KEYS.profileTheme]: false,
    [FEATURE_KEYS.profileBanner]: false,
    [FEATURE_KEYS.profileAnalytics]: false,
    [FEATURE_KEYS.verifiedBadge]: false,
    [FEATURE_KEYS.livestreamWatch]: true,
    [FEATURE_KEYS.livestreamHost]: 'limited', // 10.5h/week, 1.5h/session, public, 720p
    [FEATURE_KEYS.livestreamVod]: false,
    [FEATURE_KEYS.livestreamMembersOnly]: false,
    [FEATURE_KEYS.aiStreamCompanion]: true,
    [FEATURE_KEYS.creatorTipPage]: true, // Free monetization is live from May 2026
  },
  pro: {
    [FEATURE_KEYS.unlimitedAi]: 'limited', // Has limits but generous
    [FEATURE_KEYS.premiumAi]: false, // Can upgrade with credits
    [FEATURE_KEYS.advancedAi]: true,
    [FEATURE_KEYS.priorityAiQueue]: true,
    [FEATURE_KEYS.voiceGuidance]: false,
    [FEATURE_KEYS.multiWeekPlanning]: true,
    [FEATURE_KEYS.aiMealPlan]: true,
    [FEATURE_KEYS.recipeAnalytics]: true,
    [FEATURE_KEYS.bulkImport]: true,
    [FEATURE_KEYS.bulkAIActions]: false,
    [FEATURE_KEYS.nutritionCalculator]: 'limited', // 20/month
    [FEATURE_KEYS.recipeTranslation]: false,
    [FEATURE_KEYS.recipeImport]: 'limited', // 10/month
    [FEATURE_KEYS.familySharing]: false,
    [FEATURE_KEYS.teamAccounts]: false,
    [FEATURE_KEYS.noAds]: true,
    [FEATURE_KEYS.prioritySupport]: false,
    [FEATURE_KEYS.offlineAccess]: true,
    [FEATURE_KEYS.pinnedRecipes]: 'limited',  // 5 pins max
    [FEATURE_KEYS.profileTheme]: 'limited',   // Presets only
    [FEATURE_KEYS.profileBanner]: false,
    [FEATURE_KEYS.profileAnalytics]: 'limited', // Basic analytics
    [FEATURE_KEYS.verifiedBadge]: true,
    [FEATURE_KEYS.livestreamWatch]: true,
    [FEATURE_KEYS.livestreamHost]: 'limited', // 300 min/month, 720p
    [FEATURE_KEYS.livestreamVod]: false,
    [FEATURE_KEYS.livestreamMembersOnly]: false,
    [FEATURE_KEYS.aiStreamCompanion]: true,
    [FEATURE_KEYS.creatorTipPage]: true,
  },
  chef_master: {
    [FEATURE_KEYS.unlimitedAi]: true,
    [FEATURE_KEYS.premiumAi]: true,
    [FEATURE_KEYS.advancedAi]: true,
    [FEATURE_KEYS.priorityAiQueue]: true,
    [FEATURE_KEYS.voiceGuidance]: true,
    [FEATURE_KEYS.multiWeekPlanning]: true,
    [FEATURE_KEYS.aiMealPlan]: true,
    [FEATURE_KEYS.recipeAnalytics]: true,
    [FEATURE_KEYS.bulkImport]: true,
    [FEATURE_KEYS.bulkAIActions]: true,
    [FEATURE_KEYS.nutritionCalculator]: true,
    [FEATURE_KEYS.recipeTranslation]: true,
    [FEATURE_KEYS.recipeImport]: true,
    [FEATURE_KEYS.familySharing]: true,
    [FEATURE_KEYS.teamAccounts]: true,
    [FEATURE_KEYS.noAds]: true,
    [FEATURE_KEYS.prioritySupport]: true,
    [FEATURE_KEYS.offlineAccess]: true,
    [FEATURE_KEYS.pinnedRecipes]: true,       // 10 pins max
    [FEATURE_KEYS.profileTheme]: true,        // Presets + custom colors
    [FEATURE_KEYS.profileBanner]: true,
    [FEATURE_KEYS.profileAnalytics]: true,    // Full analytics
    [FEATURE_KEYS.verifiedBadge]: true,
    [FEATURE_KEYS.livestreamWatch]: true,
    [FEATURE_KEYS.livestreamHost]: true,       // Unlimited, 1080p
    [FEATURE_KEYS.livestreamVod]: true,
    [FEATURE_KEYS.livestreamMembersOnly]: true,
    [FEATURE_KEYS.aiStreamCompanion]: true,
    [FEATURE_KEYS.creatorTipPage]: true,
  },
};

// =============================================================================
// USAGE LIMITS
// =============================================================================

/**
 * Usage limits by tier.
 * MUST stay in sync with database function: check_usage_limits_detailed
 */
export const TIER_LIMITS: Record<SubscriptionTier, {
  credits: { monthly: number }; // -1 = unlimited
  chaosKitchen: { daily: number; monthly: number };
  cursedMode: { daily: number; monthly: number };
  recipeTransform: { daily: number; monthly: number };
  photoAnalysis: { daily: number; monthly: number };
  recipeImageGen: { daily: number; monthly: number };
  flavorPairing: { daily: number; monthly: number };
  completeRecipe: { daily: number; monthly: number };
  priceEstimate: { daily: number; monthly: number };
  recipeBookPicker: { daily: number; monthly: number };
  nutritionCalc: { monthly: number };
  recipeImport: { monthly: number };
}> = {
  free: {
    credits: { monthly: 200 },
    chaosKitchen: { daily: -1, monthly: 200 },
    cursedMode: { daily: -1, monthly: 60 },
    recipeTransform: { daily: -1, monthly: 30 },
    photoAnalysis: { daily: -1, monthly: 50 },
    recipeImageGen: { daily: -1, monthly: 20 },
    flavorPairing: { daily: -1, monthly: 30 },
    completeRecipe: { daily: -1, monthly: 30 },
    priceEstimate: { daily: -1, monthly: 20 },
    recipeBookPicker: { daily: -1, monthly: 20 },
    nutritionCalc: { monthly: 0 },
    recipeImport: { monthly: 0 },
  },
  pro: {
    credits: { monthly: 500 },
    chaosKitchen: { daily: -1, monthly: 500 },
    cursedMode: { daily: -1, monthly: 120 },
    recipeTransform: { daily: -1, monthly: 60 },
    photoAnalysis: { daily: -1, monthly: 100 },
    recipeImageGen: { daily: -1, monthly: 40 },
    flavorPairing: { daily: -1, monthly: 100 },
    completeRecipe: { daily: -1, monthly: 80 },
    priceEstimate: { daily: -1, monthly: 60 },
    recipeBookPicker: { daily: -1, monthly: 60 },
    nutritionCalc: { monthly: 20 },
    recipeImport: { monthly: 10 },
  },
  chef_master: {
    credits: { monthly: -1 }, // Unlimited
    chaosKitchen: { daily: -1, monthly: -1 },
    cursedMode: { daily: -1, monthly: -1 },
    recipeTransform: { daily: -1, monthly: -1 },
    photoAnalysis: { daily: -1, monthly: -1 },
    recipeImageGen: { daily: -1, monthly: -1 },
    flavorPairing: { daily: -1, monthly: -1 },
    completeRecipe: { daily: -1, monthly: -1 },
    priceEstimate: { daily: -1, monthly: -1 },
    recipeBookPicker: { daily: -1, monthly: -1 },
    nutritionCalc: { monthly: -1 },
    recipeImport: { monthly: -1 },
  },
};

// =============================================================================
// HELPER BENEFITS
// =============================================================================

/**
 * Helpers get 2x monthly credit limits on their tier.
 * Applied in: check_usage_limits_detailed (DB), frontend display
 */
export const HELPER_CREDIT_MULTIPLIER = 2;

/**
 * Helpers get 2x monthly support token grants.
 * Applied in: stripe-webhook grantMonthlyTokens
 */
export const HELPER_TOKEN_MULTIPLIER = 2;

/**
 * Get the effective credit limit for a tier, accounting for helper status.
 */
export function getHelperAdjustedCredits(tier: SubscriptionTier, isHelper: boolean): number {
  const base = TIER_LIMITS[tier].credits.monthly;
  if (base === -1) return -1; // unlimited stays unlimited
  return isHelper ? base * HELPER_CREDIT_MULTIPLIER : base;
}

/**
 * Guest usage limits (unauthenticated users only).
 * Signed-in users have no daily caps — only monthly credits.
 * Keep these small so guests get a taste, then sign up.
 */
export const GUEST_LIMITS = {
  chaosKitchen: 3,
  cursedMode: 2,
  recipeTransform: 1,
  photoAnalysis: 1,
  flavorPairing: 3,
  completeRecipe: 2,
  priceEstimate: 2,
  recipeBookPicker: 2,
  quickMeals: 2,
  substitutionFinder: 3,
  leftoverRescue: 2,
  cookTimeOptimizer: 2,
  onePanConverter: 2,
  batchFreezeCoach: 2,
  weeklyBudgetCook: 1,
  offerFlyerMeals: 2,
  shoppingListCleaner: 3,
  allergyKidAdapter: 2,
  nutritionGoalCook: 2,
  portionWasteGuard: 3,
  techniqueTutor: 3,
  tasteTuner: 3,
  guestMenuPairing: 2,
  surpriseDinnerWheel: 3,
  themeNightBuilder: 2,
} as const;

// =============================================================================
// AI MODELS
// =============================================================================

export type AIQualityLevel = 'basic' | 'advanced' | 'premium';

export const AI_QUALITY_BY_TIER: Record<SubscriptionTier, AIQualityLevel> = {
  free: 'basic',
  pro: 'advanced',
  chef_master: 'premium',
};

export const AI_QUALITY_DISPLAY: Record<AIQualityLevel, {
  nameKey: string;
  descKey: string;
  badgeKey: string;
  // Legacy fallback strings for non-React contexts
  name: string;
  description: string;
  badge: string;
}> = {
  basic: {
    nameKey: 'aiQuality.basic.name',
    descKey: 'aiQuality.basic.description',
    badgeKey: 'aiQuality.basic.badge',
    name: 'Basic AI',
    description: 'Fast, efficient responses',
    badge: 'Basic',
  },
  advanced: {
    nameKey: 'aiQuality.advanced.name',
    descKey: 'aiQuality.advanced.description',
    badgeKey: 'aiQuality.advanced.badge',
    name: 'Advanced AI',
    description: 'Balanced quality & speed',
    badge: 'Advanced',
  },
  premium: {
    nameKey: 'aiQuality.premium.name',
    descKey: 'aiQuality.premium.description',
    badgeKey: 'aiQuality.premium.badge',
    name: 'Premium AI',
    description: 'Highest quality responses',
    badge: 'Premium',
  },
};

// =============================================================================
// CREDIT COSTS
// =============================================================================

export const CREDIT_COSTS = {
  chaosKitchen: 1,
  christmasWizard: 1,
  cursedMode: 2,
  photoAnalysis: 5,
  recipeTransformer: 5,
  recipeImageGeneration: 5,
  swapMeal: 2,
  aiMealPlanPaidPerDay: 10,
  aiMealPlanFreePerDay: 15,
  streakFreeze: 5,
  streakRecoveryBase: 10,
  streakRecoveryPerDay: 2,
  completeRecipe: 2,
  priceEstimate: 5,
  aiRecipeBookPicker: 3,
  flavorPairing: 2,
  quickMeals: 1,
  substitutionFinder: 1,
  pantrySuggest: 1,
  receiptOcrSurplus: 5,
  leftoverRescue: 1,
  cookTimeOptimizer: 2,
  onePanConverter: 2,
  batchFreezeCoach: 1,
  weeklyBudgetCook: 3,
  offerFlyerMeals: 2,
  shoppingListCleaner: 1,
  allergyKidAdapter: 2,
  nutritionGoalCook: 2,
  portionWasteGuard: 1,
  techniqueTutor: 1,
  tasteTuner: 1,
  guestMenuPairing: 2,
  surpriseDinnerWheel: 1,
  themeNightBuilder: 2,
  recipeRemix: 1,
} as const;

// =============================================================================
// TIER BONUS CREDITS
// =============================================================================

/**
 * Bonus credit multipliers for credit purchases.
 * Pro and Chef Master users receive 40% bonus credits on all purchases.
 * This must stay in sync with: supabase/functions/_shared/discountCalculator.ts
 */
export const TIER_BONUS_CREDITS: Record<SubscriptionTier, number> = {
  free: 0,
  pro: 0.40,        // 40% bonus credits
  chef_master: 0.40, // 40% bonus credits
};

/**
 * Staff/Helper bonus on credit pack purchases (+10% additive).
 * Stacks with tier bonus: e.g. Pro helper = 40% + 10% = 50%.
 */
export const HELPER_CREDIT_PACK_BONUS = 0.10;

/**
 * Staff/Helper bonus on support token pack purchases (+5% additive).
 * Stacks with tier bonus: e.g. Chef Master helper = 15% + 5% = 20%.
 */
export const HELPER_TOKEN_PACK_BONUS = 0.05;

// =============================================================================
// STREAK RECOVERY CONFIGURATION
// =============================================================================

/**
 * Tier-based streak recovery configuration
 * - Free: 48 hours to recover, no free recoveries (must use credits)
 * - Pro: 7 days to recover, 4 free recoveries per 30-day period
 * - Chef Master: 14 days to recover, 7 free recoveries per 30-day period
 */
export const STREAK_RECOVERY_CONFIG: Record<SubscriptionTier, {
  recoveryWindowHours: number;
  freeRecoveriesPerPeriod: number;
  periodDays: number;
}> = {
  free: {
    recoveryWindowHours: 48,           // 2 days
    freeRecoveriesPerPeriod: 0,        // No free recoveries
    periodDays: 30,
  },
  pro: {
    recoveryWindowHours: 168,          // 7 days
    freeRecoveriesPerPeriod: 4,        // 4 free per month
    periodDays: 30,
  },
  chef_master: {
    recoveryWindowHours: 336,          // 14 days
    freeRecoveriesPerPeriod: 7,        // 7 free per month
    periodDays: 30,
  },
};

/**
 * Get streak recovery configuration for a tier
 */
export function getStreakRecoveryConfig(tier: SubscriptionTier) {
  return STREAK_RECOVERY_CONFIG[tier];
}

/**
 * AI quality upgrade costs by tier
 */
export const AI_UPGRADE_COSTS: Record<SubscriptionTier, Record<AIQualityLevel, number>> = {
  free: {
    basic: 0,
    advanced: 5,
    premium: 10,
  },
  pro: {
    basic: 0,
    advanced: 0,
    premium: 10,
  },
  chef_master: {
    basic: 0,
    advanced: 0,
    premium: 0,
  },
};

// =============================================================================
// TRIAL CONFIGURATION
// =============================================================================

export const TRIAL_CONFIG = {
  /** 24-hour no-card trial duration in hours */
  noCardTrialHours: 24,
  /** Stripe card trial duration in days */
  stripeTrialDays: 7,
  /** Pro trial one-time credits */
  proTrialCredits: 200,
  /** Days of Stripe Pro trial before showing Chef Master upgrade prompt */
  upgradePromptAfterDays: 4,
} as const;

// =============================================================================
// FAMILY SHARING CONFIGURATION
// =============================================================================

export const FAMILY_CONFIG = {
  maxMembers: 5,
  trialMaxMembers: 1,
  inviteExpiryDays: 7,
  maxExtraSlots: 5,
} as const;

export const TRIAL_EXTENSION_ACTIONS = {
  referral: { bonusDays: 3, maxRewards: -1, descriptionKey: 'referral.actions.referral' },
  first_recipe: { bonusDays: 1, maxRewards: 1, descriptionKey: 'referral.actions.firstRecipe' },
  newsletter: { bonusDays: 2, maxRewards: 1, descriptionKey: 'referral.actions.newsletter' },
  streak_7: { bonusDays: 2, maxRewards: 1, descriptionKey: 'referral.actions.streak7' },
} as const;

export const MAX_TRIAL_EXTENSION_DAYS = 30;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a tier has access to a feature
 */
export function tierHasFeature(tier: SubscriptionTier, feature: FeatureKey): boolean {
  const access = TIER_FEATURES[tier]?.[feature];
  return access === true || access === 'limited';
}

/**
 * Check if a feature has limited access for a tier
 */
export function tierHasLimitedAccess(tier: SubscriptionTier, feature: FeatureKey): boolean {
  return TIER_FEATURES[tier]?.[feature] === 'limited';
}

/**
 * Get the minimum tier required for a feature
 */
export function getMinTierForFeature(feature: FeatureKey): SubscriptionTier {
  for (const tier of TIERS) {
    if (TIER_FEATURES[tier][feature] === true) {
      return tier;
    }
  }
  return 'chef_master'; // Default to highest tier
}

/**
 * Get AI quality level for a tier
 */
export function getAIQualityForTier(tier: SubscriptionTier): AIQualityLevel {
  return AI_QUALITY_BY_TIER[tier];
}

/**
 * Get the cost to upgrade AI quality
 */
export function getAIUpgradeCost(tier: SubscriptionTier, targetQuality: AIQualityLevel): number {
  return AI_UPGRADE_COSTS[tier]?.[targetQuality] ?? 0;
}

/**
 * Compare two tiers
 */
export function compareTiers(a: SubscriptionTier, b: SubscriptionTier): number {
  return TIER_HIERARCHY[a] - TIER_HIERARCHY[b];
}

/**
 * Check if tier A is higher than tier B
 */
export function isHigherTier(a: SubscriptionTier, b: SubscriptionTier): boolean {
  return TIER_HIERARCHY[a] > TIER_HIERARCHY[b];
}

/**
 * Get usage limit for a feature
 */
export function getFeatureLimit(
  tier: SubscriptionTier,
  feature: keyof typeof TIER_LIMITS['free'],
  type: 'daily' | 'monthly' = 'monthly'
): number {
  const limits = TIER_LIMITS[tier]?.[feature];
  if (!limits) return 0;
  if (typeof limits === 'object' && 'monthly' in limits) {
    if (type === 'daily' && 'daily' in limits) {
      return (limits as { daily: number; monthly: number }).daily;
    }
    return limits.monthly;
  }
  return 0;
}

/**
 * Check if a feature is unlimited for a tier
 */
export function isFeatureUnlimited(
  tier: SubscriptionTier,
  feature: keyof typeof TIER_LIMITS['free'],
  type: 'daily' | 'monthly' = 'monthly'
): boolean {
  return getFeatureLimit(tier, feature, type) === -1;
}
